// The API client (feature 008 contract `http-api.md`).
//
// Unlike the web app — which is proxied through Next so its session cookie
// stays first-party — this app is a separate origin and calls the API
// directly, authenticating with a bearer token under CORS (research R5).
import { getMeta } from './db.js';

/**
 * Where the API lives. Injected at build time; falls back to the dev port on
 * the same host, which is what Chrome DevTools port forwarding produces on the
 * phone (localhost:3001 -> the laptop).
 */
const API_BASE = import.meta.env.VITE_API_BASE ?? 'http://localhost:3001';

export class ApiError extends Error {
  constructor(status, reason) {
    super(reason);
    this.status = status;
    this.reason = reason;
  }
}

/**
 * A gate phone on one bar of signal gets a socket that neither connects nor
 * fails. Without a bound, "prepare offline" hangs forever with a spinner; with
 * one it becomes `offline`, which the app already knows how to handle.
 */
const REQUEST_TIMEOUT_MS = 15000;

/**
 * How long the ONLINE check-in waits before the device decides locally
 * (feature 009, R7).
 *
 * ⚠️  Deliberately far shorter than REQUEST_TIMEOUT_MS. That one covers a sync a
 * volunteer chose to start and can watch; this one runs with a person standing
 * at a gate, where every millisecond is a queue not moving. 1200ms keeps the
 * two-second scan budget (SC-002) with room for the local path afterwards.
 *
 * A build-time variable rather than a literal because what it tolerates is
 * latency from a Sierra Leone mobile network to Frankfurt, which nobody has
 * measured — and it is the one number somebody may need to change on the day.
 */
const ONLINE_CHECKIN_TIMEOUT_MS = Number(import.meta.env.VITE_ONLINE_CHECKIN_TIMEOUT_MS ?? 1200);

/**
 * Headers that exist only to get through an ngrok tunnel.
 *
 * ngrok's free tier answers browser-like User-Agents with an HTML interstitial
 * instead of the response the caller asked for. Every request this app makes is
 * browser traffic, so against a tunnelled API *all* of them come back as HTML —
 * which surfaces as `http_200` with a JSON parse failure, or as a scan that
 * simply never resolves. Nothing in the message says "ngrok".
 *
 * This project has already paid for that lesson once: the interstitial cost two
 * WhatsApp template rejections (docs/deployment/deploy-render.md §5) because it
 * exempts `curl` — so every check made from a terminal passed while every real
 * browser request got HTML.
 *
 * Derived from the API URL rather than a separate flag, so it cannot be
 * forgotten when switching to a tunnel or left on afterwards. The header costs
 * nothing in production: these requests already carry `authorization` and a
 * JSON `content-type`, so they are preflighted regardless — but the API must
 * still allow it, which is why `modules/checkin/index.ts` lists it in
 * `allowedHeaders`. Adding it here alone produces a CORS failure that looks
 * nothing like a tunnel problem.
 */
const TUNNEL_HEADERS = (() => {
  let hostname;
  try {
    hostname = new URL(API_BASE).hostname;
  } catch {
    return {};
  }
  // Covers ngrok.io, ngrok-free.app, ngrok-free.dev and ngrok.app.
  return hostname.includes('ngrok') ? { 'ngrok-skip-browser-warning': 'true' } : {};
})();

async function request(path, init = {}) {
  let res;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  try {
    res = await fetch(`${API_BASE}${path}`, {
      ...init,
      signal: controller.signal,
      headers: {
        'content-type': 'application/json',
        ...TUNNEL_HEADERS,
        ...(init.headers ?? {}),
      },
    });
  } catch (err) {
    // No network at all, or a request that ran out of patience. Both are
    // ordinary conditions at a gate, not errors worth a stack trace — callers
    // decide what to do about them.
    throw new ApiError(0, 'offline');
  } finally {
    clearTimeout(timer);
  }

  if (!res.ok) {
    let reason = `http_${res.status}`;
    try {
      const body = await res.json();
      reason = body.error ?? reason;
    } catch {
      // Non-JSON error body — the status is all we have.
    }
    throw new ApiError(res.status, reason);
  }
  return res.json();
}

export function provision(provisioningToken) {
  return request('/checkin/provision', {
    method: 'POST',
    body: JSON.stringify({ provisioningToken }),
  });
}

export async function fetchDataset() {
  const token = await getMeta('deviceToken');
  if (!token) throw new ApiError(0, 'not_provisioned');
  return request('/checkin/sync', { headers: { authorization: `Bearer ${token}` } });
}

/**
 * The ONLINE check-in — step 1 of the §4 per-scan algorithm (feature 009).
 *
 * ⚠️  ITS OWN, MUCH SHORTER TIMEOUT. `request` waits `REQUEST_TIMEOUT_MS`, which
 * is right for a sync a volunteer chose to start and wrong for a person standing
 * at a gate: every millisecond here is a queue not moving. This gives the server
 * a brief chance and then gets out of the way.
 *
 * ⚠️  A NON-ANSWER IS NOT A REFUSAL. Timeout, no network, 5xx — all of them mean
 * "decide locally", and the caller must treat them that way. Only a 4xx with a
 * reason is the server actually saying no. Getting this backwards would turn a
 * flaky signal into refused delegates at the door (FR-005).
 */
export async function checkInOnline({ registrationId, scanId, scannedAt }) {
  const token = await getMeta('deviceToken');
  if (!token) throw new ApiError(0, 'not_provisioned');
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), ONLINE_CHECKIN_TIMEOUT_MS);
  try {
    const res = await fetch(`${API_BASE}/checkin/check-in`, {
      method: 'POST',
      signal: controller.signal,
      headers: {
        'content-type': 'application/json',
        authorization: `Bearer ${token}`,
        ...TUNNEL_HEADERS,
      },
      body: JSON.stringify({ registrationId, scanId, scannedAt }),
    });
    if (!res.ok) {
      let reason = `http_${res.status}`;
      try {
        reason = (await res.json()).error ?? reason;
      } catch {
        // Non-JSON body; the status is all we have.
      }
      throw new ApiError(res.status, reason);
    }
    return res.json();
  } catch (err) {
    if (err instanceof ApiError) throw err;
    // Aborted or unreachable. Deliberately the SAME shape the rest of this file
    // uses for "no network", so the caller has one thing to check.
    throw new ApiError(0, 'offline');
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Push the outbox and take the delta back in one response (feature 009, R5).
 *
 * One request rather than two because a gate's connectivity window may be
 * seconds long, and two round trips is two chances to half-succeed.
 */
export async function pushScans(scans, since) {
  const token = await getMeta('deviceToken');
  if (!token) throw new ApiError(0, 'not_provisioned');
  return request('/checkin/scan-events', {
    method: 'POST',
    headers: { authorization: `Bearer ${token}` },
    body: JSON.stringify({ scans, ...(since ? { since } : {}) }),
  });
}
